import React, { useMemo } from 'react';
import { Spin } from 'antd';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import ConstraintsTable from './ConstraintsTable';
import ConstraintSelector from './ConstraintSelector';
import type { ConstraintTableColumn, ConstraintLevelOption } from '../../../../config/optimizePlanConfig';
import {
    TPO_OPTIMIZE_CONSTRAINTS_ERROR,
    TPO_OPTIMIZE_CONSTRAINTS_LOADING,
    TPO_OPTIMIZE_CONSTRAINTS_TABLE,
    TPO_OPTIMIZE_LEVEL_SELECT,
    tpoOptionTestId,
} from '../../../../config/testIds';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { ConstraintRowData, Level } from './types';

interface ConstraintsTabProps {
    level: Level;
    setLevel: React.Dispatch<React.SetStateAction<Level>>;
    levelOptions: (ConstraintLevelOption & { disabled?: boolean })[];
    tableData: ConstraintRowData[];
    setTableData: React.Dispatch<React.SetStateAction<ConstraintRowData[]>>;
    tableHeaders: ConstraintTableColumn[];
    kpiList: { name: string; label: string }[];
    onKPISelectHandler: (selectedNames: string[]) => void;
    errorExists: boolean;
    constraintsLoading: boolean;
}

const ConstraintsTab: React.FC<ConstraintsTabProps> = ({
    level,
    setLevel,
    levelOptions,
    tableData,
    setTableData,
    tableHeaders,
    kpiList,
    onKPISelectHandler,
    errorExists,
    constraintsLoading,
}) => {
    const { constraintsSection, levelsLabel, selectLevelPlaceholder, tableError, emptyConstraints } =
        useTpoGenConfig().labels.optimizePlan;

    const selectedConstraintNames = useMemo(() => tableData.map((d) => d.name), [tableData]);

    const levelSelectOptions = useMemo(
        () =>
            levelOptions.map((opt) => ({
                value: opt.key,
                label: opt.label,
                disabled: opt.disabled ?? false,
                'data-testid': tpoOptionTestId(TPO_OPTIMIZE_LEVEL_SELECT, opt.key),
            })),
        [levelOptions],
    );

    return (
        <div className="h-full flex flex-col overflow-hidden border border-[rgba(240,240,240,1)] rounded">
            <div className="px-4 py-3 border-b border-[rgba(240,240,240,1)] bg-white shrink-0">
                <p className="text-sm font-semibold text-[#140b1d]">{constraintsSection}</p>
            </div>

            <div className="flex justify-between items-end px-4 py-4 bg-white shrink-0">
                <div className="flex flex-col items-start w-[248px]">
                    <label className="text-sm text-[#18181b] pb-2 leading-[22px]">{levelsLabel}</label>
                    <SelectWithInfiniteScroller
                        data-testid={TPO_OPTIMIZE_LEVEL_SELECT}
                        hasMore={false}
                        showSearch={false}
                        filterOption={false}
                        placeholder={selectLevelPlaceholder}
                        className="!h-8 !w-full"
                        showArrow
                        onChange={(val: Level) => setLevel(val)}
                        value={level}
                        options={levelSelectOptions}
                    />
                </div>
                <ConstraintSelector
                    items={kpiList}
                    selectedNames={selectedConstraintNames}
                    onApply={onKPISelectHandler}
                    disabled={!kpiList.length}
                    loading={constraintsLoading}
                />
            </div>

            <div className="flex-1 min-h-0 overflow-hidden px-4 pb-3 pt-0">
                {errorExists ? (
                    <div
                        className="flex justify-center items-center h-full text-red-600"
                        data-testid={TPO_OPTIMIZE_CONSTRAINTS_ERROR}
                    >
                        {tableError}
                    </div>
                ) : constraintsLoading ? (
                    <div
                        className="flex justify-center items-center h-full"
                        data-testid={TPO_OPTIMIZE_CONSTRAINTS_LOADING}
                    >
                        <Spin />
                    </div>
                ) : tableData.length ? (
                    <ConstraintsTable
                        columns={tableHeaders}
                        data={tableData}
                        setData={setTableData}
                        isTopLevel={levelOptions.find((o) => o.key === level)?.depthLevel === 0}
                    />
                ) : (
                    <div
                        className="flex justify-center items-center h-full text-[rgba(0,0,0,0.25)]"
                        data-testid={`${TPO_OPTIMIZE_CONSTRAINTS_TABLE}_empty`}
                    >
                        {emptyConstraints}
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(ConstraintsTab);
